import { FUSION_URL } from '../../../apiHelper/helper'
import { getFusion } from '../../../apiHelper/fusionApi'

/**
 * Updates a new DX code array with the previous non-over-writeable fields from the old DX array.
 * When dx codes update, they don't have the attached status and plan data.
 * we need that data to carry over so that it is not lost
 *
 * (Allows status and plan Progress Note fields
 * to not be overwritten on old codes when adding new codes)
 *
 * @param newDxData - The new array of Dx Codes (we added or removed one)
 * @param oldOptionsData - The old array of Dx Codes
 * @returns {array} The new array, with the fields that shouldn't be overwritten of the old array
 */
function maintainDxOptionData(newDxData, oldOptionsData) {
  let updatedArray = []

  // keep status and plan for unchanging dx codes
  newDxData?.forEach((dxItem) => {
    let existingOldItem = oldOptionsData.find(
      (oldItem) => oldItem.code && dxItem.code && oldItem.code === dxItem.code,
    )
    if (existingOldItem) {
      updatedArray.push({ ...existingOldItem, ...dxItem })
    } else {
      updatedArray.push(dxItem)
    }
  })

  return updatedArray
}

function sortDiagnosisDescription(dxA, dxB) {
  return dxA.description.localeCompare(dxB.description)
}

function sortDiagnosisCount(dxA, dxB) {
  let aCount = dxA?.count || -1
  let bCount = dxB?.count || -1
  return bCount - aCount || sortDiagnosisCode(dxA, dxB)
}

// choosing not to touch this pre-existing convoluted logic
// for sorting DxCodes
function sortDiagnosisCode(dxA, dxB) {
  if (dxA === dxB) {
    return 0
  }
  const aArr = dxA.code.split('.')
  const bArr = dxB.code.split('.')

  for (let i = 0; i < Math.min(aArr.length, bArr.length); i++) {
    if (aArr[i] < bArr[i]) {
      return -1
    }
    if (aArr[i] > bArr[i]) {
      return 1
    }
  }
  if (aArr.length < bArr.length) {
    return -1
  }
  if (aArr.length > bArr.length) {
    return 1
  }
  return 0
}

/**
 * If a DX code is in the 0th spot AND it is notValidAsMainDx
 * then it is considered invalid.
 */
function isMainDxInvalid(dxCodes) {
  if (dxCodes?.[0]) {
    return Boolean(dxCodes?.[0]?.notValidAsMainDx)
  }
  return false
}

// generate message for an invalid 0th DX code
function generateMainDXInvalidMessage(dxCodes) {
  let code = dxCodes?.[0]?.code || ''
  return `Code ${
    code.length > 3 ? code.slice(0, 3) + '.' + code.slice(3) : code
  } is not valid as a primary or stand-alone diagnosis code`
}

// sort Dx array based on chosen filter type
function sortDxByFilterType(dxList, sortType) {
  if (dxList.length > 0) {
    // default to sortDiagnosisCode
    let sortFunction = sortDiagnosisCode

    if (sortType === 'code') {
      sortFunction = sortDiagnosisCode
    } else if (sortType === 'description') {
      sortFunction = sortDiagnosisDescription
    } else if (sortType === '~count,code') {
      sortFunction = sortDiagnosisCount
    }

    return dxList?.toSorted(sortFunction)
  } else {
    return dxList
  }
}

// sort dx array so favorites are first
function sortDxByFavorites(dxList, favDxList) {
  let favDiag_list = []

  let unFavDiag_list = []

  for (let i = 0; i < dxList?.length; i++) {
    favDxList[dxList[i].code] ? favDiag_list.push(dxList[i]) : unFavDiag_list.push(dxList[i])
  }

  return [...favDiag_list, ...unFavDiag_list]
}

export async function diagnosisSearch({ searchString }) {
  try {
    if (searchString) {
      const diagnosisSearchEndpoint =
        FUSION_URL + 'v1/diagnosis/search?q=' + searchString + '&startIndex=0&recordCount=100'
      let diagnosisSearchResult = await getFusion(diagnosisSearchEndpoint)
      if (diagnosisSearchResult?.status === 'success') {
        return diagnosisSearchResult.results || []
      } else {
        console.error('Error getting DXs', diagnosisSearchResult)
      }
    }
    return []
  } catch (err) {
    console.log('getting error in rendering diagnosisSearch', err)
    return false
  }
}

export function addDxDot(dxCode) {
  if (dxCode?.includes('.')) {
    return dxCode
  }

  return dxCode.length > 3 ? [dxCode.slice(0, 3), '.', dxCode.slice(3)].join('') : dxCode
}

export {
  maintainDxOptionData,
  sortDiagnosisDescription,
  sortDiagnosisCode,
  sortDiagnosisCount,
  isMainDxInvalid,
  generateMainDXInvalidMessage,
  sortDxByFilterType,
  sortDxByFavorites,
}
